/**
 * Reproductor de una nota de audio: play/pausa, barra de progreso y tiempo.
 *
 * POR QUÉ NO `<audio controls>`: el control nativo lo pinta el sistema operativo (barra gris clara,
 * alto fijo) y adentro del lienzo oscuro del Recorrido se lee como un pedazo de otra aplicación.
 * Este es el mismo objeto con la tipografía y los colores de la casa, y hereda el color del texto
 * donde se monte — por eso sirve igual en el escenario oscuro y en la ficha clara del documento.
 *
 * ⚠️ GOTCHA — `audio.duration` de un blob de `MediaRecorder` es `Infinity` en Chrome. El contenedor
 * WebM que graba el navegador se escribe en streaming y no lleva la duración en su cabecera, así que
 * el elemento no la sabe hasta que termina de reproducirlo entero. Por eso el componente prefiere la
 * duración MEDIDA por el grabador (`durationSeconds`) y sólo usa la del elemento cuando es finita:
 * sin eso, la barra de progreso de una nota recién grabada no avanza nunca y el total dice "0:00".
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Pause, Play} from '../icons';
import {formatElapsedShort} from '../../utils/audioRecording';

const isFiniteDuration = (value) => typeof value === 'number' && isFinite(value) && value > 0;

export function AudioNotePlayer({src, durationSeconds = null, label = null, className = ''}) {
    const {t} = useTranslation();
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [currentMs, setCurrentMs] = useState(0);
    const [elementDuration, setElementDuration] = useState(null);

    const totalSeconds = isFiniteDuration(elementDuration)
        ? elementDuration
        : (isFiniteDuration(durationSeconds) ? durationSeconds : null);

    // Una nota nueva (regrabar) reinicia el player: sin esto queda el progreso de la anterior.
    useEffect(() => {
        setPlaying(false);
        setCurrentMs(0);
        setElementDuration(null);
    }, [src]);

    const toggle = useCallback(() => {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) {
            const started = el.play();
            // Autoplay bloqueado / formato no soportado: la promesa rechaza y el botón tiene que
            // volver a "reproducir", no quedarse en pausa mintiendo.
            if (started && typeof started.catch === 'function') {
                started.catch(() => setPlaying(false));
            }
        } else {
            el.pause();
        }
    }, []);

    const seekTo = useCallback((ratio) => {
        const el = audioRef.current;
        if (!el || !isFiniteDuration(totalSeconds)) return;
        const target = Math.max(0, Math.min(1, ratio)) * totalSeconds;
        try {
            el.currentTime = target;
        } catch (_) {
            /* el navegador puede rechazar el seek si todavía no bufferizó: no es un error */
        }
        setCurrentMs(target * 1000);
    }, [totalSeconds]);

    const onBarClick = useCallback((event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        if (!rect.width) return;
        seekTo((event.clientX - rect.left) / rect.width);
    }, [seekTo]);

    const onBarKeyDown = useCallback((event) => {
        if (!isFiniteDuration(totalSeconds)) return;
        const step = 5 / totalSeconds;
        const ratio = (currentMs / 1000) / totalSeconds;
        if (event.key === 'ArrowRight') { event.preventDefault(); seekTo(ratio + step); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); seekTo(ratio - step); }
        if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); toggle(); }
    }, [currentMs, seekTo, toggle, totalSeconds]);

    const percent = isFiniteDuration(totalSeconds)
        ? Math.max(0, Math.min(100, ((currentMs / 1000) / totalSeconds) * 100))
        : 0;
    const totalLabel = isFiniteDuration(totalSeconds)
        ? formatElapsedShort(totalSeconds * 1000)
        : '--:--';

    return (
        <div className={`aud-player ${className}`}>
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setCurrentMs(0); }}
                onTimeUpdate={(e) => setCurrentMs(e.target.currentTime * 1000)}
                onLoadedMetadata={(e) => setElementDuration(e.target.duration)}
                onDurationChange={(e) => setElementDuration(e.target.duration)}
            />
            <button type="button" className="aud-player__play" onClick={toggle}
                    aria-label={playing ? t('audio_player.pause') : t('audio_player.play')}>
                {playing ? <Pause size={14} weight="fill"/> : <Play size={14} weight="fill"/>}
            </button>
            <div
                className="aud-player__bar"
                role="slider"
                tabIndex={0}
                aria-label={label || t('audio_player.progress')}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(percent)}
                aria-valuetext={formatElapsedShort(currentMs)}
                onClick={onBarClick}
                onKeyDown={onBarKeyDown}
            >
                <i style={{width: `${percent}%`}}/>
            </div>
            <span className="aud-player__time">
                {formatElapsedShort(currentMs)} / {totalLabel}
            </span>
        </div>
    );
}

export default AudioNotePlayer;
